import React, { useEffect, useState, useMemo } from 'react';
import { Button, FormControl, MenuItem, Select } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { DataGrid } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import _ from 'lodash';

import ImportExcel from '../../Components/ImportExcel';
import ExportExcel from '../../Components/ExportExcel';
import ConfirmDialog from '../../Components/ConfirmDialog';
import {
  rowKeyGetter,
  autoFocusAndSelect,
  handleCopy,
} from '../../Components/ReactDataGridProps';
import ApiManageReportData from '../../Api/ApiManageReportData';
import { CurrencyFormat } from '../../Utils/Functions';

function BudgetForecastCashflowTable({
  name,
  rows,
  chartofaccounts,
  selectedForecastType,
  selectedYear,
  selectedOrganisation,
  selectedCompany, 
  onClickedAdd,
  onClickedDelete,
  onClickedAllDelete,
  getStateData,
  onClickCopy,
  handleRefetchAccounts,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [sortColumns, setSortColumns] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    deleteModal: false,
  });

  // Utility function to debug object keys
  const debugObjectKeys = (obj, label = 'Object') => {
    if (!obj) {
      console.log(`${label}: null or undefined`);
      return;
    }
    console.log(`${label} keys:`, Object.keys(obj));
    console.log(`${label} values:`, obj);
  };

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'Total':
      case 'Jan':
      case 'Feb':
      case 'Mar':
      case 'Apr':
      case 'May':
      case 'Jun':
      case 'Jul':
      case 'Aug':
      case 'Sep':
      case 'Oct':
      case 'Nov':
      case 'Dec':
        return (a, b) => {
          return a[sortColumn] > b[sortColumn];
        };

      case 'DimChartOfAccountsKey':
        return (a, b) => {
          const aName = getChartOfAccountsNameById(a[sortColumn]);
          const bName = getChartOfAccountsNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    if (rows) {
      if (rows.length > 1) {
        const sumRow = rows.reduce(multiAdd);
        sumRow.DimChartOfAccountsKey = 'Total';
        sumRow.isFooter = true;
        setTableRows([...rows, sumRow]);
      } else if (rows.length === 1) {
        const sumRow = add(rows[0]);
        sumRow.DimChartOfAccountsKey = 'Total';
        sumRow.isFooter = true;
        setTableRows([...rows, sumRow]);
      } else setTableRows(rows);
    }
  }, [rows]);

  useEffect(() => {
    setSelectedRow(null);
  }, [selectedForecastType]);

  const getChartOfAccountsNameById = (id) => {
    if (!id) return '';
    const account = chartofaccounts?.find(account => account[0] === id);
    return account ? account[3] + ' - ' + account[1] : '';
  };

  const add = (a) => {
    const sum = {};
    Object.keys(a).forEach((key) => {
      if (
        key === 'DimCompanyKey' ||
        key === 'DimOrganisationKey' ||
        key === 'id' ||
        key === 'temp_id' ||
        key === 'CreateDate' ||
        key === 'ModDate'
      )
        return (sum[key] = parseInt(a[key]) + parseInt(a[key]));
      return (sum[key] = parseInt(a[key]));
    });
    return sum;
  };

  const multiAdd = (a, b) => {
    const sum = {};
    Object.keys(a).forEach((key) => {
      return (sum[key] = parseInt(a[key]) + parseInt(b[key]));
    });
    return sum;
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handleRowChange = async (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (
      updated_data.updated[field.column.key] === '' ||
      isNaN(updated_data.updated[field.column.key])
    ) {
      toast.warning('Please insert the number value.');
      return;
    }

    if (isNaN(parseFloat(updated_data.updated[field.column.key])) === true)
      updated_data.updated[field.column.key] = 0;
    else
      updated_data.updated[field.column.key] = parseFloat(
        updated_data.updated[field.column.key],
      );

    if (field.column.key !== 'Total') {
      let newTotal =
        parseFloat(updatedRows[field.indexes[0]].Jan) +
        parseFloat(updatedRows[field.indexes[0]].Feb) +
        parseFloat(updatedRows[field.indexes[0]].Mar) +
        parseFloat(updatedRows[field.indexes[0]].Apr) +
        parseFloat(updatedRows[field.indexes[0]].May) +
        parseFloat(updatedRows[field.indexes[0]].Jun) +
        parseFloat(updatedRows[field.indexes[0]].Jul) +
        parseFloat(updatedRows[field.indexes[0]].Aug) +
        parseFloat(updatedRows[field.indexes[0]].Sep) +
        parseFloat(updatedRows[field.indexes[0]].Oct) +
        parseFloat(updatedRows[field.indexes[0]].Nov) +
        parseFloat(updatedRows[field.indexes[0]].Dec);
      updatedRows[field.indexes[0]].Total = newTotal;
      updated_data.updated = { ...updated_data.updated, Total: newTotal };
    }

    let cnt = 0;
    let currentRow = updatedRows[field.indexes[0]];
    updatedRows.forEach((row) => {
      if (
        row.DimCompanyKey === currentRow.DimCompanyKey &&
        row.DimOrganisationKey === currentRow.DimOrganisationKey &&
        row.DimChartOfAccountsKey === currentRow.DimChartOfAccountsKey &&
        row.Year === currentRow.Year
      )
        cnt++;
    });
    if (cnt > 1) return;

    let task_res = await ApiManageReportData.updateTemplate(name, updated_data);    

    if (task_res.status === 200) {
      ApiManageReportData.getTemplate(name)
        .then((res) => {
          if (res.status === 200) getStateData(res.data);
          else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message));
      setTableRows(updatedRows);
    } else toast.error(task_res.message);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = ['DimChartOfAccountsKey'];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const onClickSpread = async (ev) => {
    ev.preventDefault();
    if (!selectedRow) return;

    let updated = {};
    let val = parseFloat(selectedRow.Total) / 12;
    updated['Jan'] = val;
    updated['Feb'] = val;
    updated['Mar'] = val;
    updated['Apr'] = val;
    updated['May'] = val;
    updated['Jun'] = val;
    updated['Jul'] = val;
    updated['Aug'] = val;
    updated['Sep'] = val;
    updated['Oct'] = val;
    updated['Nov'] = val;
    updated['Dec'] = val;

    let updated_data = {
      rowIds: [selectedRow.id],
      updated: updated,
    };
    
    ApiManageReportData.updateTemplate(name, updated_data)
      .then((task_res) => {
        if (task_res.status === 200) {
          ApiManageReportData.getTemplate(name)
            .then((res) => {
              if (res.status === 200) getStateData(res.data);
              else toast.error(res.message);
            })
            .catch((error) => toast.error(error.message));
        } else toast.error(task_res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const onClickedDeleted = () => {
    if (selectedRow) {
      setOpenConfirmDialog({
        open: true,
        title: 'Delete Row',
        content: 'Do you want to really delete the selected row?',
        deleteModal: true,
      });
    }
  };

  const onClickedAllDeleted = () => {
    setOpenConfirmDialog({
      open: true,
      title: 'Delete All Rows',
      content: 'Do you want to really delete all rows?',
      deleteModal: true,
    });
  };

  const handleConfirmDelete = () => {
    if (openConfirmDialog.title === 'Delete Row') {
      onClickedDelete(name, selectedRow.id);
      setSelectedRow(null);
      setOpenConfirmDialog({
        open: false,
        title: '',
        content: '',
        deleteModal: false,
      });
    } else if (openConfirmDialog.title === 'Delete All Rows') {
      onClickedAllDelete(name, selectedForecastType, selectedYear, selectedCompany);
      setOpenConfirmDialog({
        open: false,
        title: '',
        content: '',
        deleteModal: false,
      });
    }
  };

  const handleExcelImport = async (updated_data) => {
    if (selectedOrganisation === -1) {
      toast.warning('Please select the organisation!');
      return;
    }
    
    // Debug the imported data
    debugObjectKeys(updated_data, 'Imported data array');
    if (updated_data && updated_data.length > 0) {
      debugObjectKeys(updated_data[0], 'First row data');
    }
    
    // Helper function to check if required keys exist
    const hasRequiredKeys = (obj) => {
      if (!obj) return false;
      
      const keys = Object.keys(obj);
      
      // Check for different possible key formats with trim() to handle spaces
      const hasTotal = keys.some(key => {
        const trimmedKey = key.trim();
        return trimmedKey.toLowerCase() === 'total' || 
               trimmedKey === 'Total';
      });
      
      const hasMonth1 = keys.some(key => {
        const trimmedKey = key.trim();
        return trimmedKey.toLowerCase() === 'month1' || 
               trimmedKey === 'Month1';
      });
      
      const hasMonth12 = keys.some(key => {
        const trimmedKey = key.trim();
        return trimmedKey.toLowerCase() === 'month12' || 
               trimmedKey === 'Month12';
      });
      
      return hasTotal && hasMonth1 && hasMonth12;
    };

    const toNum = (v) => {
      if (v === '' || v === null || v === undefined) return 0;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    const normalizeDataKeys = (data) => {
      if (!Array.isArray(data)) return data;

      return data.map(row => {
        const normalizedRow = {};
        Object.keys(row).forEach(key => {
          const trimmedKey = key.trim();
          let normalizedKey = trimmedKey;
          if (trimmedKey.toLowerCase() === 'month1') normalizedKey = 'Month1';
          else if (trimmedKey.toLowerCase() === 'month2') normalizedKey = 'Month2';
          else if (trimmedKey.toLowerCase() === 'month3') normalizedKey = 'Month3';
          else if (trimmedKey.toLowerCase() === 'month4') normalizedKey = 'Month4';
          else if (trimmedKey.toLowerCase() === 'month5') normalizedKey = 'Month5';
          else if (trimmedKey.toLowerCase() === 'month6') normalizedKey = 'Month6';
          else if (trimmedKey.toLowerCase() === 'month7') normalizedKey = 'Month7';
          else if (trimmedKey.toLowerCase() === 'month8') normalizedKey = 'Month8';
          else if (trimmedKey.toLowerCase() === 'month9') normalizedKey = 'Month9';
          else if (trimmedKey.toLowerCase() === 'month10') normalizedKey = 'Month10';
          else if (trimmedKey.toLowerCase() === 'month11') normalizedKey = 'Month11';
          else if (trimmedKey.toLowerCase() === 'month12') normalizedKey = 'Month12';
          else if (trimmedKey.toLowerCase() === 'total') normalizedKey = 'Total';
          normalizedRow[normalizedKey] = row[key];
        });
        ['Month1', 'Month2', 'Month3', 'Month4', 'Month5', 'Month6', 'Month7', 'Month8', 'Month9', 'Month10', 'Month11', 'Month12', 'Total'].forEach((k) => {
          if (k in normalizedRow) normalizedRow[k] = toNum(normalizedRow[k]);
        });
        return normalizedRow;
      });
    };
    
    try {
      if (hasRequiredKeys(updated_data[0])) {
        const normalizedData = normalizeDataKeys(updated_data);

        await ApiManageReportData.importDataFromExcelForBFC(
          name,
          normalizedData,
          selectedYear,
          selectedForecastType,
          selectedOrganisation,
        )
          .then((res) => {
            if (res.status === 200) {
              if (res.new_account_flag) {
                handleRefetchAccounts();
              }

              ApiManageReportData.getTemplate(name)
                .then((templateRes) => {
                  if (templateRes.status === 200) getStateData(templateRes.data);
                  else toast.error(templateRes.message);
                })
                .catch((error) => toast.error(error.message));
            }
            else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message));
      } else {
        console.log('Missing required keys. Expected: Total, Month1/Jan, Month12/Dec');
        console.log('Found keys:', Object.keys(updated_data[0] || {}));
        toast.warning('Excel file format is incorrect. Please check the column headers. Expected: Total, Month1 (or Jan), Month12 (or Dec)');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.warning(
        'Please check the excel file! This file is not correct!',
      );
    }
  };

  const tabIdx = useMemo(() => {
    let tab_idx = 0;
    if (selectedForecastType === 'Forecast') tab_idx = 1;
    else if (selectedForecastType === 'Cashflow') tab_idx = 2;
    else if (selectedForecastType === 'ProjectForecast') tab_idx = 3;
    return tab_idx;
  }, [selectedForecastType]);

  const tableColumns = useMemo(() => {
    return [
      {
        key: 'DimChartOfAccountsKey',
        name: 'Account Number',
        sortable: true,
        resizable: true,
        width: 300,
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                Total
              </div>
            );
          else
            return (
              <FormControl variant="standard">
                <Select
                  sx={{ width: 300, height: 50 }}
                  value={
                    props.row.DimChartOfAccountsKey
                      ? props.row.DimChartOfAccountsKey
                      : ''
                  }
                  onChange={(e) =>
                    props.onRowChange(
                      { ...props.row, DimChartOfAccountsKey: e.target.value },
                      true,
                    )
                  }
                >
                  {chartofaccounts &&
                    chartofaccounts.map((cur) => {
                      let idx = _.findIndex(tableRows, {
                        DimChartOfAccountsKey: cur[0],
                      });
                      if (cur[2] === true) {
                        return (
                          <MenuItem
                            key={cur[0]}
                            value={cur[0]}
                            disabled={idx >= 0}
                          >
                            {cur[3] + ' - ' + cur[1]}
                          </MenuItem>
                        );
                      } else return null;
                    })}
                </Select>
              </FormControl>
            );
        },
      },
      {
        key: 'Total',
        name: 'Total',
        sortable: true,
        resizable: true,
        width: 175,        
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div style={{ background: '#e0e0e0' }}>
              {CurrencyFormat(props.row.Total)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Total}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Total: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => (
          <div
            style={{
              background: '#e0e0e0',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {CurrencyFormat(props.row.Total)}
          </div>
        ),
      },
      {
        key: 'Jan',
        name: '1',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Jan)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Jan}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Jan: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Jan)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Jan)}</div>;
        },
      },
      {
        key: 'Feb',
        name: '2',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Feb)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Feb}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Feb: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Feb)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Feb)}</div>;
        },
      },
      {
        key: 'Mar',
        name: '3',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Mar)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Mar}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Mar: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Mar)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Mar)}</div>;
        },
      },
      {
        key: 'Apr',
        name: '4',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Apr)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Apr}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Apr: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Apr)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Apr)}</div>;
        },
      },
      {
        key: 'May',
        name: '5',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.May)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.May}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  May: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.May)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.May)}</div>;
        },
      },
      {
        key: 'Jun',
        name: '6',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Jun)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Jun}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Jun: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Jun)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Jun)}</div>;
        },
      },
      {
        key: 'Jul',
        name: '7',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Jul)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Jul}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Jul: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Jul)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Jul)}</div>;
        },
      },
      {
        key: 'Aug',
        name: '8',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Aug)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Aug}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Aug: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Aug)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Aug)}</div>;
        },
      },
      {
        key: 'Sep',
        name: '9',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Sep)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Sep}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Sep: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Sep)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Sep)}</div>;
        },
      },
      {
        key: 'Oct',
        name: '10',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Oct)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Oct}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Oct: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Oct)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Oct)}</div>;
        },
      },
      {
        key: 'Nov',
        name: '11',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Nov)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Nov}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Nov: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Nov)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Nov)}</div>;
        },
      },
      {
        key: 'Dec',
        name: '12',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Dec)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Dec}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Dec: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Dec)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Dec)}</div>;
        },
      },
    ];
  }, [tableRows, chartofaccounts]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return tableRows;

    return [...tableRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [tableRows, sortColumns]);

  const exportValue = useMemo(() => {
    let export_value = {
      [selectedForecastType]: {
        columns: [],
        data: [],
      },
      'General Ledger': {
        columns: [],
        data: [],
      },
    };

    export_value[selectedForecastType]['columns'] = [
      { title: 'AccountNumber', width: { wpx: 80 } },
      { title: 'AccountName', width: { wpx: 80 } },
      { title: 'Total', width: { wpx: 80 } },
      { title: 'Month1', width: { wpx: 80 } },
      { title: 'Month2', width: { wpx: 80 } },
      { title: 'Month3', width: { wpx: 80 } },
      { title: 'Month4', width: { wpx: 80 } },
      { title: 'Month5', width: { wpx: 80 } },
      { title: 'Month6', width: { wpx: 80 } },
      { title: 'Month7', width: { wpx: 80 } },
      { title: 'Month8', width: { wpx: 80 } },
      { title: 'Month9', width: { wpx: 80 } },
      { title: 'Month10', width: { wpx: 80 } },
      { title: 'Month11', width: { wpx: 80 } },
      { title: 'Month12', width: { wpx: 80 } },
    ];

    sortedRows.forEach((data) => {
      if (data.DimChartOfAccountsKey === 'Total') {
        export_value[selectedForecastType]['data'].push([
          { value: 'Total' },
          {
            value: '',
          },
          {
            value: data.Total ? Number(data.Total) : 0,
            style: { numFmt: '0.00' },
          },
          { value: data.Jan ? Number(data.Jan) : 0, style: { numFmt: '0.00' } },
          { value: data.Feb ? Number(data.Feb) : 0, style: { numFmt: '0.00' } },
          { value: data.Mar ? Number(data.Mar) : 0, style: { numFmt: '0.00' } },
          { value: data.Apr ? Number(data.Apr) : 0, style: { numFmt: '0.00' } },
          { value: data.May ? Number(data.May) : 0, style: { numFmt: '0.00' } },
          { value: data.Jun ? Number(data.Jun) : 0, style: { numFmt: '0.00' } },
          { value: data.Jul ? Number(data.Jul) : 0, style: { numFmt: '0.00' } },
          { value: data.Aug ? Number(data.Aug) : 0, style: { numFmt: '0.00' } },
          { value: data.Sep ? Number(data.Sep) : 0, style: { numFmt: '0.00' } },
          { value: data.Oct ? Number(data.Oct) : 0, style: { numFmt: '0.00' } },
          { value: data.Nov ? Number(data.Nov) : 0, style: { numFmt: '0.00' } },
          { value: data.Dec ? Number(data.Dec) : 0, style: { numFmt: '0.00' } },
        ]);
      } else {
        export_value[selectedForecastType]['data'].push([
          {
            value: chartofaccounts.find(
              (account) => account[0] === data.DimChartOfAccountsKey,
            )
              ? chartofaccounts.find(
                  (account) => account[0] === data.DimChartOfAccountsKey,
                )[3].toString()
              : '',
          },
          {
            value: chartofaccounts.find(
              (account) => account[0] === data.DimChartOfAccountsKey,
            )
              ? chartofaccounts.find(
                  (account) => account[0] === data.DimChartOfAccountsKey,
                )[1]
              : '',
          },
          {
            value: data.Total ? Number(data.Total) : 0,
            style: { numFmt: '0.00' },
          },
          { value: data.Jan ? Number(data.Jan) : 0, style: { numFmt: '0.00' } },
          { value: data.Feb ? Number(data.Feb) : 0, style: { numFmt: '0.00' } },
          { value: data.Mar ? Number(data.Mar) : 0, style: { numFmt: '0.00' } },
          { value: data.Apr ? Number(data.Apr) : 0, style: { numFmt: '0.00' } },
          { value: data.May ? Number(data.May) : 0, style: { numFmt: '0.00' } },
          { value: data.Jun ? Number(data.Jun) : 0, style: { numFmt: '0.00' } },
          { value: data.Jul ? Number(data.Jul) : 0, style: { numFmt: '0.00' } },
          { value: data.Aug ? Number(data.Aug) : 0, style: { numFmt: '0.00' } },
          { value: data.Sep ? Number(data.Sep) : 0, style: { numFmt: '0.00' } },
          { value: data.Oct ? Number(data.Oct) : 0, style: { numFmt: '0.00' } },
          { value: data.Nov ? Number(data.Nov) : 0, style: { numFmt: '0.00' } },
          { value: data.Dec ? Number(data.Dec) : 0, style: { numFmt: '0.00' } },
        ]);
      }
    });

    export_value['General Ledger']['columns'] = [
      { title: 'AccountNumber', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];

    export_value['General Ledger']['data'] =
      chartofaccounts &&
      chartofaccounts.map((cur) => {
        return [
          { value: cur[3] ? cur[3] : '' },
          { value: cur[1] ? cur[1] : '' },
        ];
      });

    return export_value;
  }, [selectedForecastType, sortedRows, chartofaccounts]);
  
  return (
    <div className="formClass">
      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel onImport={handleExcelImport} tabIdx={tabIdx} />
        <ExportExcel filename={selectedForecastType} value={exportValue} />
        <Button
          onClick={onClickSpread}
          variant="contained"
          color="primary"
          size="small"
          style={{ marginLeft: 8, marginRight: 8 }}
        >
          Spread
        </Button>
        {(selectedForecastType === 'Forecast' ||
          selectedForecastType === 'Cashflow') && (
          <Button
            onClick={() => onClickCopy(selectedForecastType)}
            variant="contained"
            color="secondary"
            size="small"
          >
            Copy
          </Button>
        )}
        <IconButton
          aria-label="Add"
          onClick={() => {
            onClickedAdd(name, selectedRow ? selectedRow.temp_id : 0);
          }}
        >
          <AddIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={onClickedDeleted}>
          <DeleteIcon />
        </IconButton>
        <IconButton aria-label="deleteAll" onClick={onClickedAllDeleted}>
          <DeleteSweepIcon />
        </IconButton>
      </div>

      <DataGrid
        rowKeyGetter={rowKeyGetter}
        className="fill-grid rdg-light"
        columns={tableColumns}
        rows={sortedRows}
        enableCellSelect={true}
        sortColumns={sortColumns}
        onSortColumnsChange={setSortColumns}
        onRowsChange={handleRowChange}
        onCellClick={handleCellClick}
        onCopy={handleCopy}
        onPaste={handlePaste}
        rowHeight={50}
        style={{ maxHeight: window.innerHeight - 233 }}
      />

      <ConfirmDialog
        open={openConfirmDialog.open}
        title={openConfirmDialog.title}
        content={openConfirmDialog.content}
        deleteModal={openConfirmDialog.deleteModal}
        onClose={() => setOpenConfirmDialog({ ...openConfirmDialog, open: false })}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

BudgetForecastCashflowTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  chartofaccounts: PropTypes.array.isRequired,
  selectedForecastType: PropTypes.string.isRequired,
  selectedYear: PropTypes.number.isRequired,
  selectedOrganisation: PropTypes.number.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  onClickedAllDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
  onClickCopy: PropTypes.func.isRequired,
  handleRefetchAccounts: PropTypes.func.isRequired,
};

export default BudgetForecastCashflowTable;
